import { createSlice } from '@reduxjs/toolkit'
import { keyBy } from '../../../../helpers/common'
import { register, syncInitialState } from '../../../../helpers/reducerRegistry'
import { COMPANY_TYPES } from '../../../../utils/common'
import { convertDataHighlight, getDetailPanel, getKeyPanels } from '../helper'
import {
  getDefaultTemplateForLayoutThunk,
  getDefaultTemplateThunk,
  getHighlightThunk,
  getIndicatorPanel,
  getTemplateDetailThunk,
  getTemplatesSearchThunk,
  getTemplatesThunk,
} from './thunk'

export const keyLoading = {
  TEMPLATE: 'template',
  DEFAULT_TEMPLATE: 'defaultTemplate',
  DEFAULT_TEMPLATE_FOR_LAYOUT: 'defaultTemplateForLayout',
  TEMPLATE_SEARCH: 'templateSearch',
  HIGHLIGHT: 'highlight',
  INDICATOR: 'indicator',
}

const initialState = {
  panelById: {},
  panelId: [],
  templateName: '',
  templateId: null,
  isFiinGateTemplate: false,
  loading: {
    [keyLoading.TEMPLATE]: false,
    [keyLoading.DEFAULT_TEMPLATE]: false,
    [keyLoading.DEFAULT_TEMPLATE_FOR_LAYOUT]: false,
    [keyLoading.TEMPLATE_SEARCH]: false,
    [keyLoading.HIGHLIGHT]: false,
    [keyLoading.INDICATOR]: false,
  },
  templates: [],
  totalRecord: 0,
  defaultTemplate: {},
  templateSearch: {
    [COMPANY_TYPES.CORPORATE]: [],
    [COMPANY_TYPES.BANK]: [],
    [COMPANY_TYPES.NON_BANK]: [],
    [COMPANY_TYPES.SECURITIES]: [],
    [COMPANY_TYPES.LIFE_INSURANCE]: [],
    [COMPANY_TYPES.NON_LIFE_INSURANCE]: [],
  },
  highlight: {},
  isNotHasChange: true,
  indicatorById: {},
  currentTemplate: {
    panelId: [],
    panelById: {},
  },
}

const name = 'companyAnalysis/financialAnalysis'
const persistKeys = [
  'panelById',
  'panelId',
  'templateName',
  'isFiinGateTemplate',
  'templateId',
  'isNotHasChange',
]

export const slice = createSlice({
  name,
  initialState: syncInitialState({ name, initialState, persistKeys }),
  reducers: {
    resetFullStore() {
      return initialState
    },
    resetStore(state) {
      Object.keys(initialState).forEach((key) => {
        if (!persistKeys.includes) {
          state[key] = initialState[key]
        }
      })
    },
    changeViewBy(state, action) {
      const { id, viewBy } = action.payload
      state.panelById[id].viewByActive = viewBy
    },
    changeChecked(state, action) {
      const { id, checked } = action.payload
      if (checked?.length) {
        state.panelById[id].setting.checked = checked
      }
    },
    changeYear(state, action) {
      const { id, year } = action.payload
      if (state.panelById[id]?.setting) {
        state.panelById[id].setting.year = year
      }
    },
    applySetting(state, action) {
      const { templateId, templateName, panels, isFiinGateTemplate } =
        action.payload
      state.templateId = templateId
      state.templateName = templateName
      state.panelId = panels.map((panel) => panel.id)
      state.panelById = keyBy(panels, 'id')
      state.isFiinGateTemplate = isFiinGateTemplate
    },
    changePanelId(state, action) {
      state.panelId = action.payload
      Object.keys(state.panelById).forEach((id) => {
        if (!action.payload.includes(id)) {
          delete state.panelById[id]
        }
      })
      state.isNotHasChange = false
    },
    changeFiinGateTemplateName(state, action) {
      state.templateName = action.payload
    },
    setIsNotHasChange(state, action) {
      state.isNotHasChange = action.payload
    },
  },
  extraReducers: (builder) => {
    // get template
    builder.addCase(getTemplatesThunk.pending, (state) => {
      state.loading[keyLoading.TEMPLATE] = true
    })
    builder.addCase(getTemplatesThunk.fulfilled, (state, action) => {
      state.templates = action.payload.data
      state.totalRecord = action.payload.totalRecords

      state.loading[keyLoading.TEMPLATE] = false
    })
    builder.addCase(getTemplatesThunk.rejected, (state, action) => {
      state.loading[keyLoading.TEMPLATE] = action.payload
    })
    // get default template
    builder.addCase(getDefaultTemplateThunk.pending, (state) => {
      state.loading[keyLoading.DEFAULT_TEMPLATE] = true
    })
    builder.addCase(getDefaultTemplateThunk.fulfilled, (state, action) => {
      state.defaultTemplate = action.payload

      state.loading[keyLoading.DEFAULT_TEMPLATE] = false
    })
    builder.addCase(getDefaultTemplateThunk.rejected, (state, action) => {
      state.loading[keyLoading.DEFAULT_TEMPLATE] = action.payload
    })
    // get default template for layut
    builder.addCase(getDefaultTemplateForLayoutThunk.pending, (state) => {
      state.loading[keyLoading.DEFAULT_TEMPLATE_FOR_LAYOUT] = true
    })
    builder.addCase(
      getDefaultTemplateForLayoutThunk.fulfilled,
      (state, action) => {
        const { result, companyType } = action.payload
        state.templateName = result.financialAnalysisTemplateName
        state.templateId = result.financialAnalysisTemplateId
        state.isFiinGateTemplate = result.isFiinGateTemplate
        if (result?.financialAnalysisTemplateData) {
          const data = JSON.parse(result.financialAnalysisTemplateData)
          state.panelId = data.map((panel) => panel.id)
          state.panelById = keyBy(data, 'id')
        } else {
          const keysPanel = getKeyPanels(companyType)
          state.panelById = {}
          state.panelId = []
          Object.values(keysPanel).forEach((key) => {
            const panel = getDetailPanel(key)
            state.panelById[panel.id] = panel
            state.panelId.push(panel.id)
          })
        }

        state.loading[keyLoading.DEFAULT_TEMPLATE_FOR_LAYOUT] = false
      },
    )
    builder.addCase(getDefaultTemplateForLayoutThunk.rejected, (state) => {
      state.loading[keyLoading.DEFAULT_TEMPLATE_FOR_LAYOUT] = false
    })
    // get template
    builder.addCase(getTemplatesSearchThunk.pending, (state) => {
      state.loading[keyLoading.TEMPLATE_SEARCH] = true
    })
    builder.addCase(getTemplatesSearchThunk.fulfilled, (state, action) => {
      state.templateSearch = action.payload

      state.loading[keyLoading.TEMPLATE_SEARCH] = false
    })
    builder.addCase(getTemplatesSearchThunk.rejected, (state, action) => {
      state.loading[keyLoading.TEMPLATE_SEARCH] = action.payload
    })
    // get highlight
    builder.addCase(getHighlightThunk.pending, (state) => {
      state.loading[keyLoading.HIGHLIGHT] = true
    })
    builder.addCase(getHighlightThunk.fulfilled, (state, action) => {
      state.highlight = convertDataHighlight(action.payload)

      state.loading[keyLoading.HIGHLIGHT] = false
    })
    builder.addCase(getHighlightThunk.rejected, (state) => {
      state.loading[keyLoading.HIGHLIGHT] = false
    })
    // get indicator panel
    builder.addCase(getIndicatorPanel.pending, (state) => {
      state.loading[keyLoading.INDICATOR] = true
    })
    builder.addCase(getIndicatorPanel.fulfilled, (state, action) => {
      state.indicatorById = keyBy(action.payload, 'ratioCode')
      state.loading[keyLoading.INDICATOR] = false
    })
    builder.addCase(getIndicatorPanel.rejected, (state, action) => {
      state.loading[keyLoading.INDICATOR] = action.payload
    })
    // current template
    builder.addCase(getTemplateDetailThunk.fulfilled, (state, action) => {
      const { data, companyType } = action.payload
      if (data?.financialAnalysisTemplateData) {
        const panels = JSON.parse(data.financialAnalysisTemplateData)
        state.currentTemplate.panelId = panels.map((panel) => panel.id)
        state.currentTemplate.panelById = keyBy(panels, 'id')
      } else {
        const keysPanel = getKeyPanels(companyType)
        state.currentTemplate.panelById = {}
        state.currentTemplate.panelId = []
        Object.values(keysPanel).forEach((key) => {
          const panel = getDetailPanel(key)
          state.currentTemplate.panelById[panel.id] = panel
          state.currentTemplate.panelId.push(panel.id)
        })
      }
    })
  },
})

export const selectLoading = (key) => (state) => state[slice.name].loading[key]

export const selectPanelId = (state) => state[slice.name].panelId

export const selectPanelById = (state) => state[slice.name].panelById

export const selectDetailPanel = (id) => (state) =>
  state[slice.name].panelById[id]

export const selectChecked = (id) => (state) =>
  state[slice.name].panelById[id]?.setting?.checked

export const selectYear = (id, staticYear) => (state) =>
  staticYear || state[slice.name].panelById[id]?.setting?.year

export const selectTemplates = (state) => state[slice.name].templates

export const selectTotalRecord = (state) => state[slice.name].totalRecord

export const selectTemplateName = (state) => state[slice.name].templateName

export const selectTemplateId = (state) => state[slice.name].templateId

export const selectDefaultTemplate = (state) =>
  state[slice.name].defaultTemplate

export const selectTemplateSearch = (state) => state[slice.name].templateSearch

export const selectHighlight = (state) => state[slice.name].highlight

export const selectIsNotHasChange = (state) => state[slice.name].isNotHasChange

export const selectIsFiinGateTemplate = (state) =>
  state[slice.name].isFiinGateTemplate

export const selectIndicatorById = (state) => state[slice.name].indicatorById

export const selectCurrentTemplate = (state) =>
  state[slice.name].currentTemplate

export const selectSliceFinancialAnalysis = (state) => state[slice.name]

export const {
  resetStore,
  resetFullStore,
  changeViewBy,
  changeChecked,
  changeYear,
  applySetting,
  changePanelId,
  changeFiinGateTemplateName,
  setIsNotHasChange,
} = slice.actions

register(slice.name, slice.reducer, persistKeys)
